import React, { useActionState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import styled from "styled-components";
import Button from "../../components/button/Button.tsx";
import Input from "../../components/form-item/Input.tsx";
import SvgLoadingAnimation from "../../components/loading/index.tsx";
import Modal from "../../components/modal/Modal";
import { AuthErrorMessage } from "../../components/layouts/authStyles";
import { useSigninUserMutation } from "../../app/accounts/authuser";
import { setAuthTokenToCookies } from "../../common/utils";

const ForgotPasswordLink = styled(Link)`
  display: block;
  text-align: center;
  color: var(--soremo-light-gray);
  text-decoration: none;
  font-size: 13px;
  &:hover {
    color: var(--soremo-blue);
  }
`;

type SigninState = { error: string };

/** APIエラーレスポンスからユーザー向けメッセージを抽出する */
const extractApiMessage = (error: unknown): string => {
    const errorData = (error as any)?.data;
    if (!errorData) return "";
    if (typeof errorData === "string") return errorData;
    if (typeof errorData === "object") {
        if (typeof errorData.message === "string") return errorData.message;
        if (typeof errorData.detail === "string") return errorData.detail;
        if (typeof errorData.error === "string") return errorData.error;
        if (Array.isArray(errorData.error) && typeof errorData.error[0] === "string") return errorData.error[0];
        if (Array.isArray(errorData.non_field_errors) && typeof errorData.non_field_errors[0] === "string") return errorData.non_field_errors[0];
    }
    return "";
};

/** HTTPステータスに応じたエラーメッセージを返す */
const getErrorMessageByStatus = (status: number | string, apiMessage: string): string => {
    switch (status) {
        case 500:
            return "一時的な問題が発生しました。時間をおいて再度お試しください。";
        case 401:
            return apiMessage || "メールアドレスまたはパスワードをご確認ください。";
        case 400:
            return apiMessage || "入力内容に誤りがあります。ご確認ください。";
        default:
            return apiMessage || "サインインに失敗しました。再度お試しください。";
    }
};

/** redirect クエリの値を内部パスのみ許可する（オープンリダイレクト対策） */
const sanitizeRedirect = (raw: string | null): string => {
    if (raw && raw.startsWith("/") && !raw.startsWith("//")) return raw;
    return "/projects";
};

const Signin: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const redirectTo = sanitizeRedirect(searchParams.get("redirect"));
    const [loginUser] = useSigninUserMutation();

    const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
        if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault();
            e.currentTarget.requestSubmit();
        }
    };

    const [state, formAction, isPending] = useActionState(async (_prevState: SigninState, formData: FormData) => {
        const username = formData.get("username") as string;
        const password = formData.get("password") as string;

        try {
            // remember_me は送らない（フォームに UI がなく、BE 既定の false で
            // セッション 24 時間失効となる）。
            const result = await loginUser({ username, password });

            if (result.data?.token) {
                setAuthTokenToCookies(result.data.token);
                // replace で遷移し、サインインページを履歴に残さない。
                // （遷移先で「戻る」を押した際にサインインへ戻るのを防ぐ）
                navigate(redirectTo, { replace: true });
                return { error: "" };
            }

            if (result.error) {
                const apiMessage = extractApiMessage(result.error);
                const status = "status" in result.error ? result.error.status : 0;
                return { error: getErrorMessageByStatus(status, apiMessage) };
            }
        } catch (err) {
            console.error("エラー:", err);
            return { error: "ネットワークエラーが発生しました。インターネット接続を確認してください。" };
        }

        return { error: "" };
    }, { error: "" });

    return (
        <>
            <Modal variant="auth">
                <form action={formAction} onKeyDown={handleKeyDown}>
                    <div className="u-col u-gap32">
                        {state?.error && <AuthErrorMessage>{state.error}</AuthErrorMessage>}

                        <div className="u-w100 u-col u-gap16">
                            <Input
                                label=""
                                type="text"
                                placeholder="メールアドレス"
                                name="username"
                                option="required"
                                autocomplete="username"
                            />
                            <Input
                                label=""
                                type="password"
                                placeholder="パスワード"
                                name="password"
                                option="required"
                                autocomplete="current-password"
                            />
                        </div>
                        <Button
                            type="submit"
                            theme="primary"
                            variant="filled"
                            size="large"
                            width="full"
                            rightIcon="chevron_right"
                            disabled={isPending}
                        >
                            {isPending ? "サインイン中..." : "サインイン"}
                        </Button>
                        <ForgotPasswordLink to="/password-reset">
                            パスワードをお忘れですか？
                        </ForgotPasswordLink>
                    </div>
                </form>
            </Modal>
            {isPending && <SvgLoadingAnimation />}
        </>
    );
};

export default Signin; 